import type { ReactNode } from 'react';
import { ActionIcon, Badge, Box, Divider, Group, Text, Title, Tooltip } from '@mantine/core';
import { IconBook, IconSettings } from '@tabler/icons-react';
import { Link } from 'wouter';
import { Fragment } from 'react';
import { paths } from '../app/routes';
import { StatusPill } from './StatusPill';
import { useKnowledge } from '../features/knowledge/KnowledgeDrawer';
import { AccountButton } from '../features/account/AccountButton';

/** Same build everywhere; the staging Worker's address is what marks it. */
const IS_STAGING = typeof location !== 'undefined' && /^(flow-staging\.|staging\.)/.test(location.hostname);

export interface Crumb {
  label: string;
  href: string;
}

/**
 * Every page uses this frame: consistent header, actions, and content width.
 * `crumbs` are the pages above this one (Tournaments › Golden Gate), each a link; `title` is this page.
 */
export const PageShell = ({ title, subtitle, actions, children, wide, crumbs = [] }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; wide?: boolean; crumbs?: Crumb[] }) => {
  const knowledge = useKnowledge();
  return (
    // Wide pages (a round) fill exactly the screen and lay out their own scrolling; others grow and scroll.
    <Box mih="100%" h={wide ? '100dvh' : undefined} style={{ display: 'flex', flexDirection: 'column' }}>
      <Group justify="space-between" px="md" py={8} wrap="nowrap" style={{ borderBottom: '1px solid var(--grid-line)', gap: 12 }}>
        <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
          <Link href={paths.dashboard} aria-label="flow: your tournaments" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <img src="/icon.svg" alt="" width={26} height={26} style={{ display: 'block' }} />
            <Text fw={700} size="lg" visibleFrom="xs" style={{ letterSpacing: '-0.02em' }}>flow</Text>
            {IS_STAGING && <Badge color="orange" variant="filled" size="sm" data-testid="staging-badge">staging</Badge>}
          </Link>
          <Divider orientation="vertical" />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
              {crumbs.length > 0 && (
                <Box component="nav" aria-label="Breadcrumb" data-testid="breadcrumbs" style={{ display: 'flex', gap: 6, alignItems: 'baseline', minWidth: 0, flexShrink: 1, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  {crumbs.map((c) => (
                    <Fragment key={c.href}>
                      <Text component={Link} href={c.href} size="sm" c="dimmed" truncate style={{ textDecoration: 'none', maxWidth: 180 }}>{c.label}</Text>
                      <Text size="sm" c="dimmed" aria-hidden>›</Text>
                    </Fragment>
                  ))}
                </Box>
              )}
              <Title order={1} size="h5" fw={600} lineClamp={1} style={{ flexShrink: 0, maxWidth: '100%' }}>{title}</Title>
            </Group>
            {subtitle && <Text size="xs" c="dimmed" lineClamp={1}>{subtitle}</Text>}
          </Box>
        </Group>
        <Group gap={4} wrap="nowrap">
          {actions}
          <StatusPill />
          <Tooltip label="Glossary & cheatsheet (⌘G)">
            <ActionIcon aria-label="Glossary and cheatsheet" onClick={() => knowledge.open()} size="lg">
              <IconBook size={18} />
            </ActionIcon>
          </Tooltip>
          <AccountButton />
          <Tooltip label="Settings">
            <ActionIcon component={Link} href={paths.settings()} aria-label="Settings" size="lg">
              <IconSettings size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <Box style={{ flex: 1, minHeight: 0, ...(wide ? { display: 'flex', flexDirection: 'column' } : {}) }} px={wide ? 0 : 'md'} py={wide ? 0 : 'md'} maw={wide ? undefined : 960} w="100%" mx="auto">
        {children}
      </Box>
    </Box>
  );
};

export const Section = ({ title, children, right, id }: { title: ReactNode; children: ReactNode; right?: ReactNode; id?: string }) => (
  <Box mb="lg" id={id} style={{ scrollMarginTop: 12 }}>
    <Group justify="space-between" mb={6}>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>{title}</Text>
      {right}
    </Group>
    {children}
  </Box>
);
